import ReportIssueBar from '@/components/ReportIssueBar';
import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import toast from 'react-hot-toast';
import { ArrowLeft, Download, FileText, Upload, X, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MatrixBackground from "@/MatrixBackground";
import AnimatedToolTitle from '@/components/AnimatedToolTitle';
import AnimatedActionButton from '@/components/AnimatedActionButton';
import LedProgressLine from "@/components/LedProgressLine";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getToken = () => localStorage.getItem('token');

const letterTypes = [
  {
    id: "demand",
    name: "Letter of Demand",
    description: "Initial formal demand for payment with specific deadline"
  },
  {
    id: "final-notice",
    name: "Final Notice",
    description: "Final warning before legal proceedings commence"
  },
  {
    id: "legal-proceedings",
    name: "Legal Proceedings Notice",
    description: "Notice of intention to commence legal action"
  },
  {
    id: "settlement",
    name: "Settlement Offer",
    description: "Proposal for debt settlement with payment terms"
  },
  {
    id: "payment-plan",
    name: "Payment Plan Agreement",
    description: "Structured payment arrangement document"
  }
];

const DebtRecoveryTools = ({ onBack }) => {
    // Set page title
  useEffect(() => {
    document.title = "Debt Recovery Tools - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    letterType: '',
    debtorName: '',
    debtorAddress: '',
    creditorName: '',
    creditorAddress: '',
    debtAmount: '',
    debtDescription: '',
    originalDate: '',
    additionalDetails: ''
  });
  const [generating, setGenerating] = useState(false);
  const [generatedLetter, setGeneratedLetter] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [templateFile, setTemplateFile] = useState(null);
  const [templateUploaded, setTemplateUploaded] = useState(false);
  const [showTemplateStep, setShowTemplateStep] = useState(true);

  const handleTemplateUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a PDF or Word document');
        return;
      }
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      setTemplateFile(file);
      toast.success('Template uploaded successfully!');
    }
  };

  const handleSkipTemplate = () => {
    setShowTemplateStep(false);
  };

  const handleContinueWithTemplate = () => {
    if (templateFile) {
      setTemplateUploaded(true);
      setShowTemplateStep(false);
    } else {
      toast.error('Please upload a template file first');
    }
  };

  const handleLetterSelect = (letter) => {
    setSelectedLetter(letter);
    setShowTemplateStep(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGenerate = async () => {
    if (!formData.letterType || !formData.debtorName || !formData.debtAmount) {
      toast.error('Please fill in all required fields');
      return;
    }

    setGenerating(true);

    try {
      // Prepare form data for file upload
      const requestData = new FormData();
      requestData.append('letterType', formData.letterType);
      requestData.append('debtorName', formData.debtorName);
      requestData.append('debtorAddress', formData.debtorAddress);
      requestData.append('creditorName', formData.creditorName);
      requestData.append('creditorAddress', formData.creditorAddress);
      requestData.append('debtAmount', formData.debtAmount);
      requestData.append('debtDescription', formData.debtDescription);
      requestData.append('originalDate', formData.originalDate);
      requestData.append('additionalDetails', formData.additionalDetails);
      
      // Add template file if uploaded
      if (templateFile) {
        requestData.append('template', templateFile);
        requestData.append('useTemplate', 'true');
      }

      const response = await axios.post(
        `${API}/debt-recovery/generate-letter`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setGeneratedLetter(response.data);
      toast.success('Debt recovery letter generated successfully!');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error generating letter. Please try again.';
      toast.error(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedLetter) return;

    const blob = new Blob([generatedLetter.content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.letterType}_${formData.debtorName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success('Letter downloaded!');
  };

  const handleCopyToClipboard = () => {
    if (!generatedLetter) return;
    navigator.clipboard.writeText(generatedLetter.content);
    toast.success('Letter copied to clipboard!');
  };

  const handleReset = () => {
    setFormData({
      letterType: '',
      debtorName: '',
      debtorAddress: '',
      creditorName: '',
      creditorAddress: '',
      debtAmount: '',
      debtDescription: '',
      originalDate: '',
      additionalDetails: ''
    });
    setGeneratedLetter(null);
    setTemplateFile(null);
    setTemplateUploaded(false);
    setShowTemplateStep(true);
  };

  return (
    <div className="tool-page">
      <MatrixBackground />
      <div className="tool-header">
        <Button 
          variant="ghost" 
          onClick={() => onBack ? onBack() : navigate('/dashboard')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>
        <div className="tool-title-section">
          <FileText size={40} className="tool-icon gold-icon" />
          <div>
            <AnimatedToolTitle title="DEBT RECOVERY TOOLS" />
            <p className="tool-subtitle">Generate professional debt recovery letters with your branding using Lawra's AI assistance</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        {showTemplateStep ? (
          /* Template Upload Step */
          <Card className="template-upload-card">
            <CardHeader>
              <CardTitle style={{ color: '#1a1a1a' }}>Template Upload (Optional)</CardTitle>
              <CardDescription>
                Upload your law firm's branded template to maintain your company identity, or skip to generate a standard debt recovery letter
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="template-upload-section">
                <div className="file-upload-area">
                  <input
                    type="file"
                    id="template-upload"
                    accept=".pdf,.docx,.doc"
                    onChange={handleTemplateUpload}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="template-upload" className="file-upload-label">
                    <div className="upload-icon-tool">
                      {templateFile ? <FileText size={48} /> : <Upload size={48} />}
                    </div>
                    <p className="upload-text-tool">
                      {templateFile ? templateFile.name : 'Click to upload any branded document (PDF or DOCX) for Lawra to use'}
                    </p>
                    <p className="upload-subtext-tool">Maximum file size: 10MB</p>
                  </label>
                </div>
                
                {templateFile && (
                  <div className="template-file-preview" style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'rgba(212, 175, 55, 0.1)', borderRadius: '8px', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FileText size={16} style={{ color: '#D4AF37' }} />
                        <span style={{ color: '#1a1a1a', fontSize: '0.9rem' }}>{templateFile.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setTemplateFile(null);
                          document.getElementById('template-upload').value = '';
                        }}
                        style={{ color: '#D4AF37' }}
                      >
                        <X size={16} />
                      </Button>
                    </div>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem', marginTop: '0.5rem', marginBottom: '0' }}>
                      Your branding and formatting will be preserved in the generated letter
                    </p>
                  </div>
                )}

                {!templateFile && (
                  <div style={{ textAlign: 'center', margin: '1.5rem 0', color: '#1a1a1a', fontSize: '1rem', fontWeight: '500' }}>
                    Or
                  </div>
                )}

                <div className="template-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: templateFile ? '2rem' : '0' }}>
                  {templateFile && (
                    <AnimatedActionButton
                      onClick={handleContinueWithTemplate}
                      icon={<FileText size={20} />}
                    >
                      Continue with Template
                    </AnimatedActionButton>
                  )}
                  {!templateFile && (
                    <AnimatedActionButton
                      onClick={handleSkipTemplate}
                      icon={<FileText size={20} />}
                    >
                      Generate Standard Letter
                    </AnimatedActionButton>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Main Letter Generation Form */
          <Card className="generation-form-card">
            <CardHeader>
              <CardTitle>Debt Recovery Letter Details</CardTitle>
              <CardDescription>
                Fill in the details to generate your professional debt recovery letter
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="form-grid" style={{ display: 'grid', gap: '1.5rem' }}>
                <div className="form-group">
                  <Label htmlFor="letterType">Letter Type *</Label>
                  <Select
                    value={formData.letterType}
                    onValueChange={(value) => handleInputChange('letterType', value)}
                  >
                    <SelectTrigger id="letterType">
                      <SelectValue placeholder="Select letter type" />
                    </SelectTrigger>
                    <SelectContent>
                      {letterTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name} - {type.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <Label htmlFor="debtorName">Debtor Name *</Label>
                    <Input
                      id="debtorName"
                      placeholder="John Smith"
                      value={formData.debtorName}
                      onChange={(e) => handleInputChange('debtorName', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="creditorName">Creditor Name</Label>
                    <Input
                      id="creditorName"
                      placeholder="Your Company Name"
                      value={formData.creditorName}
                      onChange={(e) => handleInputChange('creditorName', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <Label htmlFor="debtAmount">Debt Amount *</Label>
                    <Input
                      id="debtAmount"
                      placeholder="£1,500.00"
                      value={formData.debtAmount}
                      onChange={(e) => handleInputChange('debtAmount', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="originalDate">Original Date</Label>
                    <Input
                      id="originalDate"
                      type="date"
                      value={formData.originalDate}
                      onChange={(e) => handleInputChange('originalDate', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <Label htmlFor="debtorAddress">Debtor Address</Label>
                  <Textarea
                    id="debtorAddress"
                    placeholder="123 Main Street, London, UK, SW1A 1AA"
                    value={formData.debtorAddress}
                    onChange={(e) => handleInputChange('debtorAddress', e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="debtDescription">Debt Description</Label>
                  <Textarea
                    id="debtDescription"
                    placeholder="Description of goods/services provided"
                    value={formData.debtDescription}
                    onChange={(e) => handleInputChange('debtDescription', e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="additionalDetails">Additional Details (Optional)</Label>
                  <Textarea
                    id="additionalDetails"
                    placeholder="Any additional terms, conditions, or specific details for the letter"
                    value={formData.additionalDetails}
                    onChange={(e) => handleInputChange('additionalDetails', e.target.value)}
                    rows={3}
                  />
                </div>
              </div>

              <div className="form-actions">
                <AnimatedActionButton
                  onClick={handleGenerate}
                  disabled={!formData.letterType || !formData.debtorName || !formData.debtAmount || generating}
                  loading={generating}
                  icon={<Sparkles size={20} />}
                  size="lg"
                >
                  {generating ? 'Generating Letter...' : 'Generate Debt Recovery Letter'}
                </AnimatedActionButton>
                <LedProgressLine isActive={generating} />
              </div>
            </CardContent>
          </Card>
        )}

        {generatedLetter && (
          <Card className="result-card">
            <CardHeader>
              <div className="result-header">
                <div>
                  <CardTitle className="result-title">Generated Letter</CardTitle>
                  <CardDescription className="result-subtitle">
                    Your debt recovery letter has been generated
                  </CardDescription>
                </div>
                <div className="result-actions">
                  <Button onClick={handleDownload} variant="outline">
                    <Download size={18} style={{ marginRight: '8px' }} />
                    Download
                  </Button>
                  <Button onClick={handleReset} variant="outline">
                    Generate New Letter
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="letter-preview">
                <div className="letter-header">
                  <h3>Letter Content Preview</h3>
                </div>
                <div className="letter-content">
                  <pre>{generatedLetter.letter_content || generatedLetter.content}</pre>
                </div>
        <ReportIssueBar toolName="Debt Recovery" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DebtRecoveryTools;
